import { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff, Copy, Check, KeyRound, ShieldCheck } from 'lucide-react';
import API from '../../lib/api';

const SECRET_LABELS = {
  income_tax_password: 'Income Tax Portal Password',
  income_tax_login:    'Income Tax Portal Password',
  gst_login:           'GST Portal Password',
  pf_login:            'EPFO Portal Password',
  esi_login:           'ESIC Portal Password',
  tds_login:           'TRACES Portal Password',
};

function humanise(key) {
  return SECRET_LABELS[key]
    || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <button
      onClick={onCopy}
      type="button"
      className="text-[#9CA3AF] hover:text-[#6C63FF] transition-colors"
      title={copied ? 'Copied' : 'Copy'}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function SecretRow({ label, value }) {
  // Auto-hide 30s after reveal — security default; user can re-Show.
  const [shown, setShown] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (shown) {
      timerRef.current = setTimeout(() => setShown(false), 30_000);
      return () => clearTimeout(timerRef.current);
    }
  }, [shown]);

  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-[#E8E6F8] last:border-0">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-body font-bold">{label}</p>
        <p className="font-mono text-sm text-[#1A1A2E] truncate">
          {shown ? value : '••••••••••'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => setShown((s) => !s)}
          type="button"
          className="text-xs font-body text-[#6C63FF] hover:underline flex items-center gap-1"
          title={shown ? 'Hide' : 'Show (auto-hides in 30s)'}
        >
          {shown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {shown ? 'Hide' : 'Show'}
        </button>
        {shown && <CopyButton value={value} />}
      </div>
    </div>
  );
}

function PlainRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-[#E8E6F8] last:border-0">
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-body font-bold">{label}</p>
        <p className="font-mono text-sm text-[#1A1A2E] truncate">{value || '–'}</p>
      </div>
      {value && <CopyButton value={value} />}
    </div>
  );
}

export default function ClientCredentialsCard({ orderId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!orderId) return;
    let active = true;
    setLoading(true);
    setError('');
    API.get(`/admin/orders/${orderId}/credentials`)
      .then(({ data }) => active && setData(data))
      .catch((err) => active && setError(
        err.response?.status === 403 ? 'You do not have access to client credentials for this order.' :
        err.response?.data?.detail   || 'Failed to load credentials.'
      ))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [orderId]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <div className="flex items-center justify-center py-6">
          <div className="w-5 h-5 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-5">
        <p className="text-sm text-red-500 font-body">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const pans = [
    data.pan_number   && { label: 'PAN',           value: data.pan_number },
    data.business_pan && { label: 'Business PAN',  value: data.business_pan },
  ].filter(Boolean);

  const secretEntries = Object.entries(data.secrets || {});
  const hasAnything   = pans.length > 0 || secretEntries.length > 0;

  return (
    <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="h-4 w-4 text-[#6C63FF]" />
        <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">Client Credentials</h3>
      </div>

      {!hasAnything ? (
        <p className="text-xs text-[#9CA3AF] font-body">
          No PAN or portal credentials submitted by the client yet.
        </p>
      ) : (
        <>
          {pans.map((p) => (
            <PlainRow key={p.label} label={p.label} value={p.value} />
          ))}
          {secretEntries.map(([k, v]) => (
            <SecretRow key={k} label={humanise(k)} value={v} />
          ))}

          <div className="mt-3 flex items-start gap-2 text-[10px] text-[#9CA3AF] font-body">
            <ShieldCheck className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>
              Credentials are stored encrypted (AES-256-GCM). Reveal events are recorded
              in activity logs and the value auto-hides after 30 seconds.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
